// MultiFactorAuthentication.jsx (First time MFA setup)
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import VerificationInput from 'react-verification-input';
import { useHistory } from 'react-router-dom';
import { Alert as ErrorAlert, Spinner } from 'react-bootstrap';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { enableMFA, UPDATE_MFA_STATUS, verifyMFASetup } from '../../actions/auth';
import { AccountContentContainer } from './common';

const useStyles = makeStyles({
    container: {
        width: '100% !important',
        justifyContent: 'space-between',
    },
    character: {
        width: '48px !important',
        height: '52px',
        lineHeight: '52px',
        fontSize: '22px !important',
        backgroundColor: '#fff !important',
        border: '1px solid #00AEEF !important',
        borderRadius: '10px',
        marginLeft: '0px !important',
        color: '#0C2945',
        marginLeft: '8px'
    },
});

const Container = styled.div`
    max-width: 600px;
    margin: 0 auto;
    padding: 40px 20px;
`;

export const Heading = styled.h2`
    color: #0C2945;
    font-size: 24px;
    line-height: 32px;
    font-weight: 700;
    margin-bottom: 12px;
`;

export const Text = styled.p`
    color: #5B6575;
    font-size: 14px;
    line-height: 24px;
    margin-bottom: 24px;
`;

export const QRContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
`;

export const QRImage = styled.img`
    width: 220px;
    height: 220px;
    border-radius: 16px;
    border: 1px solid #E6ECF2;
    padding: 12px;
    background: white;
`;

export const SecretContainer = styled.div`
    width: 100%;
    background: #F7FAFC;
    border: 1px solid #E4EDF5;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 32px;
`;

export const Label = styled.p`
    font-size: 13px;
    font-weight: 600;
    color: #0C2945;
    margin-bottom: 8px;
`;

export const SecretKey = styled.div`
    font-size: 15px;
    word-break: break-all;
    color: #00AEEF;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: monospace;
    
    &:hover {
        opacity: 0.8;
    }
`;

export const CopyIcon = styled.span`
    font-size: 12px;
    color: #5B6575;
    margin-left: 8px;
`;

export const VerificationContainer = styled.div`
    width: 100%;
    margin-bottom: 24px;
`;

export const WarningBox = styled.div`
    width: 100%;
    background: #FFF8E6;
    border: 1px solid #FFE2A8;
    border-radius: 12px;
    padding: 14px 16px;
    margin-top: 24px;
    margin-bottom: 24px;
`;

export const WarningTitle = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: #8A5B00;
    margin-bottom: 6px;
`;

export const WarningText = styled.p`
    font-size: 13px;
    line-height: 20px;
    color: #8A5B00;
    margin: 0;
`;

export const RecoveryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    width: 100%;
    margin-top: 24px;
    margin-bottom: 24px;
`;

export const RecoveryCode = styled.div`
    background: #F7FAFC;
    border: 1px solid #E4EDF5;
    border-radius: 10px;
    padding: 14px;
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    color: #0C2945;
    font-family: monospace;
`;

export const ActionButtons = styled.div`
    width: 100%;
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
`;

export const SecondaryButton = styled.button`
    flex: 1;
    height: 48px;
    border-radius: 12px;
    border: 1px solid #00AEEF;
    background: white;
    color: #00AEEF;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        opacity: 0.9;
        background: #f0f9ff;
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

export const SubmitButton = styled.button`
    width: 100%;
    height: 48px;
    border-radius: 12px;
    border: none;
    background: #00AEEF;
    color: white;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        opacity: 0.9;
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

function MultiFactorAuthentication() {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const { tempToken } = useSelector(state => state.auth);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [step, setStep] = useState(() => {
        const savedStep = sessionStorage.getItem('mfa_setup_step');
        return savedStep === 'recovery' ? 'recovery' : 'setup';
    });
    const [qrCode, setQrCode] = useState(() => { '';
    });
    const [recoveryCodes, setRecoveryCodes] = useState(() => {
        const savedCodes = sessionStorage.getItem('mfa_recovery_codes');
        return savedCodes ? JSON.parse(savedCodes) : [];
    });
    const [secretKey, setSecretKey] = useState(() => {''});

    useEffect(() => {
        if (step === 'recovery') {
            sessionStorage.setItem('mfa_setup_step', 'recovery');
            sessionStorage.setItem('mfa_recovery_codes', JSON.stringify(recoveryCodes));
        }
    }, [step, recoveryCodes, qrCode, secretKey]);

    useEffect(() => {
     
        if (step === 'recovery' && recoveryCodes.length > 0) {
            return;
        }
        
        if (!tempToken) {
            history.push('/login');
            return;
        }
        
        if (step !== 'recovery' && !qrCode) {
            enableMFAAndGetQR();
        }
    }, []);

    const enableMFAAndGetQR = async () => {
        setLoading(true);
        setError('');
        
        const result = await dispatch(enableMFA(tempToken));
        if (result.success) {
            setQrCode(result.data.qr_code);
            
            setSecretKey(result.data.secret_key);
            
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    const verifyMFA = async () => {
        if (verificationCode.length !== 6) {
            setError('Please enter a valid 6-digit code');
            return;
        }

        setLoading(true);
        setError('');
        
        const result = await dispatch(verifyMFASetup(verificationCode, tempToken));
        
        if (result.success) {
            const codes = result.backup_codes;
            setRecoveryCodes(codes);
            sessionStorage.setItem('mfa_recovery_codes', JSON.stringify(codes));
            setSuccess('MFA enabled successfully!');
            setStep('recovery');
            sessionStorage.setItem('mfa_setup_step', 'recovery');
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    const copySecretKey = () => {
        if (secretKey) {
            navigator.clipboard.writeText(secretKey);
            setSuccess('Secret key copied to clipboard!');
            setTimeout(() => setSuccess(''), 2000);
        }
    };

    const copyRecoveryCodes = async () => {
        try {
            const codesWithSpaces = recoveryCodes.map(code => code + ' ').join('\n');
            await navigator.clipboard.writeText(codesWithSpaces);
            setSuccess('Recovery codes copied to clipboard!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to copy codes');
        }
    };

    const downloadRecoveryCodes = () => {
        const content = `MFA Recovery Codes\n\n${recoveryCodes.join('\n')}\n\nKeep these codes safe. Each code can only be used once.`;
        
        const blob = new Blob([content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        
        a.href = url;
        a.download = `mfa-recovery-codes-${Date.now()}.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        setSuccess('Recovery codes downloaded!');
        setTimeout(() => setSuccess(''), 3000);
    };

    const continueToVerification = () => {
        sessionStorage.removeItem('mfa_setup_step');
        sessionStorage.removeItem('mfa_recovery_codes');
        
        dispatch({ 
            type: UPDATE_MFA_STATUS, 
            payload: { mfaEnabled: true } 
        });
        history.push('/multi-factor-verification');
    };

    const getQRCodeSrc = () => {
        if (!qrCode) return '';
        if (qrCode.startsWith('data:image')) {
            return qrCode;
        }
        if (qrCode.startsWith('http')) {
            return qrCode;
        }
        return `data:image/png;base64,${qrCode}`;
    };

    return (
        <AccountContentContainer>
            <Container>
                {loading && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                )}

                {error && (
                    <ErrorAlert variant="danger" onClose={() => setError('')} dismissible>
                        {error}
                    </ErrorAlert>
                )}

                {success && !error && (
                    <ErrorAlert variant="success" onClose={() => setSuccess('')} dismissible>
                        {success}
                    </ErrorAlert>
                )}

                {step === 'setup' && (
                    <>
                        <Heading>
                            Set Up Multi-Factor Authentication
                        </Heading>

                        <Text>
                            Enhance your account security by setting up two-factor authentication.
                            Scan the QR code below using Google Authenticator, Authy, etc.
                        </Text>

                        {qrCode && (
                            <QRContainer>
                                <QRImage 
                                    src={getQRCodeSrc()} 
                                    alt="MFA QR Code"
                                    onError={(e) => {
                                        console.error('QR Code failed to load');
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </QRContainer>
                        )}

                        {secretKey && (
                            <SecretContainer>
                                <Label>
                                    Manual Setup Key
                                    <span style={{ fontSize: '12px', color: '#5B6575', marginLeft: '8px' }}>
                                        (if you can't scan the QR code)
                                    </span>
                                </Label>
                                <SecretKey onClick={copySecretKey}>
                                    <span>{secretKey}</span>
                                    <CopyIcon>
                                        {success.includes('copied') ? '✓ Copied!' : '📋 Copy'}
                                    </CopyIcon>
                                </SecretKey>
                            </SecretContainer>
                        )}

                        <VerificationContainer>
                            <Label>Enter 6-digit verification code</Label>
                            <VerificationInput
                                classNames={{
                                    container: classes.container,
                                    character: classes.character,
                                }}
                                value={verificationCode}
                                onChange={(value) => setVerificationCode(value)}
                                autoFocus
                                placeholder=""
                                validChars="0-9"
                            />
                        </VerificationContainer>

                        <WarningBox>
                            <WarningTitle>
                                ⚠️ Important
                            </WarningTitle>
                            <WarningText>
                                • Save your recovery codes when provided in the next step<br/>
                                • If you lose access to your authenticator device, you'll need recovery codes to regain access<br/>
                                • Never share your verification codes with anyone<br/>
                                • Each verification code expires after 30 seconds
                            </WarningText>
                        </WarningBox>

                        <SubmitButton
                            onClick={verifyMFA}
                            disabled={verificationCode.length !== 6 || loading}
                        >
                            {loading ? 'Verifying...' : 'Verify & Enable MFA'}
                        </SubmitButton>
                    </>
                )}

                {step === 'recovery' && (
                    <>
                        <Heading>
                            Save Your Recovery Codes
                        </Heading>

                        <Text>
                            These recovery codes can be used to access your account if you lose your 
                            authenticator device. Save them in a secure location.
                        </Text>

                        <RecoveryGrid>
                            {recoveryCodes.map((code, index) => (
                                <RecoveryCode key={index}>
                                    {code}
                                </RecoveryCode>
                            ))}
                        </RecoveryGrid>

                        <ActionButtons>
                            <SecondaryButton onClick={copyRecoveryCodes}>
                                📋 Copy All Codes
                            </SecondaryButton>
                            <SecondaryButton onClick={downloadRecoveryCodes}>
                                💾 Download Codes
                            </SecondaryButton>
                        </ActionButtons>

                        <WarningBox>
                            <WarningTitle>
                                ⚠️ Important Security Notice
                            </WarningTitle>
                            <WarningText>
                                • These recovery codes will only be shown once<br/>
                                • Each recovery code can only be used once<br/>
                                • Store them securely (password manager, safe, or print them)<br/>
                                • Don't take screenshots or photos of these codes
                            </WarningText>
                        </WarningBox>

                        <SubmitButton onClick={continueToVerification}>
                            I've Saved My Recovery Codes
                        </SubmitButton>
                    </>
                )}
            </Container>
        </AccountContentContainer>
    );
}

export default MultiFactorAuthentication;